import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { BasicPagination, Carousel } from "@repo/components/src/ui/carousel";
import { UserNicheProps } from "../types";

import { useComponentWidth } from "@repo/components/src/hooks/width";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { PlatformCodeEnum } from "@repo/graphql/gql/graphql";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { ComponentRef, useCallback, useMemo, useRef } from "react";
import { useSharedValue } from "react-native-reanimated";
import ImageContent from "./image";
import ThumbnailLink from "./thumbnailLink";
import { MediaViewerProps } from "./types";
import VideoContent from "./video";

type Props = {
  userNiche: UserNicheProps["userNicheSet"][number];
};

const PostCard = (props: Props) => {
  const { userNiche: userNicheFragment } = props;
  const post = userNicheFragment.post;
  const { width, layoutSetWidth } = useComponentWidth();
  const ref = useRef<ComponentRef<typeof Carousel>>(null);
  const progress = useSharedValue<number>(0);
  const onPressPagination = useCallback((index: number) => {
    ref.current?.scrollTo({ index, animated: true });
  }, []);
  const prev = useCallback(() => {
    ref.current?.prev();
  }, []);
  const next = useCallback(() => {
    ref.current?.next();
  }, []);

  const aspectRatio = useMemo(() => {
    switch (post?.postType.platformCode) {
      case "REELS":
      case "VIDEO":
        return "aspect-reel";
      case "FEED":
        return "aspect-feed";
      default:
        return "aspect-feed";
    }
  }, [post]);

  const renderItem = useCallback(
    (
      postContentFragment: NonNullable<
        UserNicheProps["userNicheSet"][number]["post"]
      >["postcontentSet"][number],
    ) => {
      if (!post) return null;
      const postContent = postContentFragment;
      const socialNetworkUser = post.socialNetworkUser;
      const thumbnail = post.thumbnail;
      const postContentPlatformCode = postContent.postContentType.platformCode;
      const props: MediaViewerProps = {
        postContent: postContentFragment,
        thumbnail,
        post,
      };
      if (!postContent.content) return <ThumbnailLink {...props} />;
      if (socialNetworkUser.platform.enumCode === PlatformCodeEnum.TikTok) {
        return <ThumbnailLink {...props} />;
      }

      switch (postContentPlatformCode) {
        case "VIDEO":
          return <VideoContent {...props} />;
        case "IMAGE":
          return <ImageContent {...props} />;
        case "CAROUSEL_ALBUM":
          return <ImageContent {...props} />;
        default:
          return <></>;
      }
    },
    [post],
  );

  const niche = userNicheFragment.niche;
  if (!post) return null;
  return (
    <Box className="p-0">
      <Box className="p-3 shadow-soft-1 rounded-lg h-full relative">
        <Badge action="muted" variant="outline" className="w-fit">
          <BadgeText>{niche.name.display}</BadgeText>
        </Badge>
        <Box className="w-full relative mt-3" onLayout={layoutSetWidth}>
          {width != null ? (
            <Carousel
              key={width}
              ref={ref}
              data={post.postcontentSet}
              loop={true}
              itemSize={width}
              scrollEnabled={post.postcontentSet.length > 1}
              className="aspect-reel"
              containerClassName="bg-background-900 rounded-xl overflow-hidden"
              onConfigurePanGesture={(g) => {
                "worklet";
                g.activeOffsetX([-10, 10]);
              }}
              progress={progress}
              renderItem={(info) => {
                return (
                  <Box className={`${aspectRatio} my-auto overflow-hidden`}>
                    {renderItem(info.item)}
                  </Box>
                );
              }}
            />
          ) : null}
        </Box>
        {post.postcontentSet.length > 1 && (
          <>
            <Box className="absolute z-10 bottom-12 w-full flex-row justify-center">
              <BasicPagination
                progress={progress}
                count={post.postcontentSet.length}
                onPress={onPressPagination}
                dotClassName="rounded-full w-12 h-12 bg-background-600"
                activeDotClassName="bg-background-0"
                containerClassName="gap-1"
              />
            </Box>
            <Button
              variant="link"
              size="none"
              onPress={next}
              className="absolute top-1/2 -translate-y-1/2 right-6"
            >
              <ButtonIcon
                as={ChevronRight}
                className="text-background-0 w-6 h-6"
              />
            </Button>
            <Button
              variant="link"
              size="none"
              onPress={prev}
              className="absolute top-1/2 -translate-y-1/2 left-6"
            >
              <ButtonIcon
                as={ChevronLeft}
                className="text-background-0 w-6 h-6"
              />
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PostCard;
